import {
  Button,
  DialogForm,
  Form,
  Label,
  Alert,
  AlertDescription,
} from '@ais-ui/ui-core';
import React from 'react';

import { cn } from '@ais-ui/utils';
import { MutateRouteForm } from './MutateRouteForm';
import { useLocalization } from '@ais-ui/locale-management';
import { PiBoat } from 'react-icons/pi';
import { ShipInfo, TargetInfoType } from '@ais-ui/websockets';
import { useTargetChooser } from '../../Map/contexts';
import { useRoutes } from '../RoutesContext';

interface RouteDialogProps {
  mutateType: 'edit' | 'create';
}

export const RouteDialog = ({ mutateType }: RouteDialogProps) => {
  const { messages } = useLocalization();
  const dialogTitle =
    mutateType === 'create'
      ? messages.routesCreateTitle
      : messages.routesEditTitle;

  const {
    showDialog,
    setShowDialog,
    isSubmitting,
    submitError,
    form,
    onCreateRoute,
  } = useRoutes();

  const {
    setShowChooseTargetPanel,
    onChooseTarget,
    onRejectTarget,
    setChooseTargetTypes,
  } = useTargetChooser();

  const handleOpenChange = (isOpen: boolean) => {
    setShowDialog(isOpen);
  };

  const onSelectShipOnMap = () => {
    setShowDialog(false);
    setShowChooseTargetPanel(true);
    setChooseTargetTypes(['ship']);

    onChooseTarget.current = (target: TargetInfoType) => {
      const ship = target as ShipInfo;
      form.setValue('sourceShip.name', ship.name);
      form.setValue('sourceShip.imo', ship.imo);
      form.setValue('sourceShip.type', ship.shipType);
      form.setValue('sourceShip.mmsi', ship.mmsi);
      form.setValue('sourceShip.callSign', ship.callSign);
      setShowDialog(true);
      setShowChooseTargetPanel(false);
    };

    onRejectTarget.current = () => {
      setShowDialog(true);
      setShowChooseTargetPanel(false);
    };
  };

  return (
    <Form {...form}>
      <DialogForm
        showDialog={showDialog}
        onOpenChange={handleOpenChange}
        size="md"
      >
        <DialogForm.Header>
          <DialogForm.Title icon={<PiBoat className="size-5" />}>
            {dialogTitle}
          </DialogForm.Title>
        </DialogForm.Header>
        <DialogForm.Body observeResize={false}>
          <div className="space-y-6">
            {submitError && (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}
            <div
              className={cn(
                'flex flex-row items-center justify-between border rounded-lg p-4',
              )}
            >
              <div className="space-y-0.5">
                <Label className="text-base">Select vessel</Label>
                <p className="text-[0.8rem] text-muted-foreground">
                  Select vessel on the map
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onSelectShipOnMap}
              >
                <PiBoat className="size-5" />
              </Button>
            </div>
            <MutateRouteForm form={form}></MutateRouteForm>
          </div>
        </DialogForm.Body>
        <DialogForm.Footer>
          <DialogForm.Cancel>{messages.genericCancel}</DialogForm.Cancel>
          <DialogForm.Submit
            id="submit-route-button"
            disabled={isSubmitting}
            onSubmit={form.handleSubmit(onCreateRoute)}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </DialogForm.Submit>
        </DialogForm.Footer>
      </DialogForm>
    </Form>
  );
};
